/**
 * Lab bet-ticker-modal.html — list BTS Excel jobs + download links.
 */

import { Button, Modal, Spinner, StateBlock } from '@w7/ui'
import { useTickerExcelList } from '@/api/hooks/bet-ticker/useTickerExcel'
import { ll } from '@/lib/format'

const PAGE_SIZE_HINT = 10

function formatReportDay(raw: string | null): string {
  if (!raw) return '—'
  const d = new Date(raw)
  return Number.isNaN(d.getTime()) ? raw : ll(d)
}

export function TickerExcelModal({
  open,
  onClose,
  page,
  onPage,
}: {
  open: boolean
  onClose: () => void
  page: number
  onPage: (page: number) => void
}) {
  const listQ = useTickerExcelList(page, open)
  const reports = listQ.data?.reports ?? []
  const disableNext = reports.length < PAGE_SIZE_HINT

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Download Excel"
      size="lg"
      data-testid="ticker-excel-modal"
    >
      {listQ.isLoading ? (
        <div className="w7-bm-excel-loading">
          <Spinner />
        </div>
      ) : listQ.isError ? (
        <StateBlock tone="error" title="Could not load Excel jobs">
          {listQ.error instanceof Error ? listQ.error.message : 'Request failed'}
        </StateBlock>
      ) : reports.length === 0 ? (
        <StateBlock tone="neutral" title="No data found">
          Generate Excel first, then refresh.
        </StateBlock>
      ) : (
        <div className="w7-bm-excel-table-wrap">
          <table className="w7-bm-excel-table">
            <thead>
              <tr>
                <th>From Date</th>
                <th>To Date</th>
                <th>Current Status</th>
                <th>Download</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((row, i) => (
                <tr key={`${row.fromDate}-${row.toDate}-${i}`}>
                  <td>{formatReportDay(row.fromDate)}</td>
                  <td>{formatReportDay(row.toDate)}</td>
                  <td>{row.currentStatus || '—'}</td>
                  <td>
                    {row.downloadUrl ? (
                      <a
                        className="w7-bm-excel-dl"
                        href={row.downloadUrl}
                        target="_blank"
                        rel="noreferrer"
                        download
                      >
                        Download
                      </a>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="w7-bm-excel-pager">
        <Button
          type="button"
          variant="secondary"
          disabled={page <= 1 || listQ.isFetching}
          onClick={() => onPage(page - 1)}
        >
          Previous
        </Button>
        <span className="w7-bm-excel-page">{page}</span>
        <Button
          type="button"
          variant="secondary"
          disabled={disableNext || listQ.isFetching}
          onClick={() => onPage(page + 1)}
        >
          Next
        </Button>
        <Button
          type="button"
          variant="ghost"
          icon="refresh"
          busy={listQ.isFetching}
          aria-label="Refresh Excel list"
          onClick={() => void listQ.refetch()}
        >
          Refresh
        </Button>
      </div>
    </Modal>
  )
}
